// Curated list of IANA timezones we surface in operator + site forms.
// Order matters — most-likely-to-be-picked first so the user doesn't have
// to scroll. Add to this list if a new market needs it.
//
// We render each entry with its current UTC offset (e.g. "America/New_York
// (GMT-04:00)") so users don't have to guess. The offset is computed
// live from the browser's Intl.DateTimeFormat — that handles DST changes
// without us hard-coding a static table that goes stale twice a year.

export const COMMON_TIMEZONES: ReadonlyArray<string> = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tbilisi',     // Georgia (the country) — first ARMS market outside the US
  'UTC',
];

/**
 * Returns "GMT-04:00" (or "GMT" for UTC) for the given IANA zone, computed
 * from a representative date so DST is honored. Intl.DateTimeFormat with
 * timeZoneName: 'longOffset' is the standard way to get this — supported
 * everywhere the portal runs (modern Chromium / Firefox / Safari).
 */
export function gmtOffsetFor(timeZone: string, when: Date = new Date()): string {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longOffset',
    });
    // The longOffset name appears as a token in formatToParts; pluck it out.
    const tzNamePart = fmt.formatToParts(when).find((p) => p.type === 'timeZoneName');
    return tzNamePart?.value ?? 'GMT';
  } catch {
    // Bad zone name → no offset rather than crashing the dropdown.
    return '';
  }
}

/**
 * "America/New_York (GMT-04:00)" — the form-friendly label used in
 * select dropdowns. Offset reflects the current moment, so it'll show
 * EDT in summer and EST in winter without code changes.
 */
export function timeZoneLabel(timeZone: string, when: Date = new Date()): string {
  const offset = gmtOffsetFor(timeZone, when);
  return offset ? `${timeZone} (${offset})` : timeZone;
}
